import { AlertCircle, Check, Loader2, Save } from 'lucide-react';
import { cn } from '../lib/cn';

// ─────────────────────────────────────────────────────────────────────────────
// SaveBar — the ONE save affordance every multi-field editor mounts at the
// bottom of its form (Lisa, 2026-07-19: "save buttons seem hidden ... normally
// at the BOTTOM after all the possible edits are done, not in between. Plus
// an obvious confirmation that something HAS saved.").
//
// Sticky to the viewport bottom while the editor is in view, so the save
// action is never scrolled out of sight mid-edit — and visible ONLY while
// there's something to report (dirty edits, an in-flight save, or a
// just-completed one), unmounting cleanly the rest of the time so it never
// occupies dead space on a pristine form.
//
// Callers own their dirty/saving/saved state — usually: dirty = current form
// !== a snapshot of the last-loaded/last-saved draft; saving =
// mutation.isPending; saved = a local flag flipped true onSuccess and
// cleared after ~2.5s (the existing convention already used across the
// editors this replaces). This component never invents parallel state, it
// only renders what it's told.
//
// Stacking (Lisa, 2026-07-19): `bottom-[var(--activation-bar-h,0px)]` lets a
// page that ALSO renders its own sticky bottom bar (Tournament Setup's
// activation bar, carrying the comp lifecycle controls) reserve room simply
// by setting `--activation-bar-h` on an ancestor — the CSS var cascades
// through the DOM regardless of how deep this editor is nested, no prop
// drilling needed. Every other page never sets it, so it defaults to 0px —
// exactly the old `bottom-0` behaviour, unchanged.
// ─────────────────────────────────────────────────────────────────────────────

export interface SaveBarProps {
  /** Form has unsaved edits relative to the last loaded/saved snapshot. */
  dirty: boolean;
  /** Save mutation is in flight. */
  saving: boolean;
  /** A save just completed successfully — shows the quiet "Saved" state. */
  saved: boolean;
  /** Save is blocked by something other than dirty/saving (e.g. validation). */
  disabled?: boolean;
  error?: string | null;
  onSave: () => void;
  /** Only wire this where a true local revert-to-snapshot already exists. */
  onDiscard?: () => void;
  saveLabel?: string;
  /** Copy shown on the button while `saving` is true (default "Saving…"). */
  savingLabel?: string;
  className?: string;
}

export function SaveBar({
  dirty,
  saving,
  saved,
  disabled = false,
  error,
  onSave,
  onDiscard,
  saveLabel = 'Save changes',
  savingLabel = 'Saving…',
  className,
}: SaveBarProps) {
  const visible = dirty || saving || saved;
  if (!visible) return null;

  // Once a save completes and there's nothing new dirty, drop to the quiet
  // "Saved" state — the bar itself hides ~2.5s later (caller's timer flips
  // `saved` back to false), matching the pre-existing flash convention.
  const showSavedOnly = saved && !saving && !dirty;

  return (
    <div
      className={cn(
        // SOLID grey plate, no blur — matches PageActionBar's fixed bottom
        // bar (lib/pageActions.tsx), which commit 1b5b182 moved to this same
        // treatment after the translucent/backdrop-blur version read as
        // "hard to see" (Lisa, 2026-07-19).
        'sticky bottom-[var(--activation-bar-h,0px)] z-30 border-t border-iron-700 bg-iron-900 px-4 py-3',
        'pb-[calc(0.75rem+env(safe-area-inset-bottom))]',
        className,
      )}
    >
      <div className="flex flex-wrap items-center gap-3">
        {showSavedOnly ? (
          <span className="flex items-center gap-1.5 font-display text-[10px] font-black uppercase tracking-wider text-emerald-400">
            <Check className="h-3.5 w-3.5" strokeWidth={2.5} />
            Saved
          </span>
        ) : (
          <>
            {!saving && (
              <span className="font-display text-[10px] font-black uppercase tracking-wider text-iron-400">
                Unsaved changes
              </span>
            )}
            <button
              type="button"
              onClick={onSave}
              disabled={saving || disabled}
              className="btn-primary flex items-center gap-2 disabled:opacity-50"
            >
              {saving ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={2.5} />
                  {savingLabel}
                </>
              ) : (
                <>
                  <Save className="h-3.5 w-3.5" strokeWidth={2.5} />
                  {saveLabel}
                </>
              )}
            </button>
            {onDiscard && (
              <button
                type="button"
                onClick={onDiscard}
                disabled={saving}
                className="border border-iron-800 px-3 py-1.5 font-display text-[10px] font-black uppercase tracking-wider text-iron-400 transition-colors hover:border-iron-600 hover:text-iron-200 disabled:opacity-50"
              >
                Discard
              </button>
            )}
          </>
        )}

        {error && (
          <div className="flex items-center gap-1.5 text-xs text-red-300">
            <AlertCircle className="h-3.5 w-3.5 shrink-0" strokeWidth={2.5} />
            {error}
          </div>
        )}
      </div>
    </div>
  );
}
